import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SnackbarComponent, ToastType } from '../../../../shared/components/layout/snackbar/snackbar.component';
import { AuthStore } from '../../../../api/auth/auth.store';
import { VerifyType } from '../verify-email/verify-email.component';
import { TotpService } from '../../../../api/services/totp.service';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    RouterLink,
    MatFormFieldModule,
    ReactiveFormsModule,
    NgClass,
    MatInputModule,
    MatIconModule,
    MatSnackBarModule,
    NgxMaskDirective,
  ],
})
export class LoginComponent implements OnInit {
  private authStore = inject(AuthStore);

  form: FormGroup = new FormGroup({});
  hide = true;
  disabled = false;
  disableFlag = false;
  loginSuccessful = false;
  is2faSetup = true;
  qrCode: any;

  is2FaRequired: boolean = true;

  downloadApp: boolean = true;
  setupVerifyAuthCode: boolean = false;
  verifyAuthCode: boolean = false;
  verifyEmail: boolean = false;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private totpService: TotpService,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      email: this.fb.control(null, [Validators.required, Validators.email]),
      password: this.fb.control(null, Validators.required),
      verificationCode: this.fb.control(null),
    });
  }

  async onSubmit() {
    if (!this.form.valid) {
      return;
    }

    this.disabled = true;
    try {
      const result = await this.authStore.loginValidate(this.form.value.email, this.form.value.password);

      if (!result.success) {
        this.verifyEmail = false;
        if (result.error?.verify_email) {
          this.verifyEmail = true;
        } else if (result.error?.deactivated_user) {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Error, message: 'Your account has been deactivated' },
          });
        } else {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Error, message: result.error?.message || 'Invalid username or password' },
          });
        }
        return;
      }

      this.qrCode = result.qrBlob ?? new Blob([]);
      if (this.qrCode.size > 0) {
        this.is2faSetup = false;
        this.loginSuccessful = true;
        this.downloadApp = true;
      } else {
        this.is2faSetup = true;
        this.verifyAuthCode = true;
        this.loginSuccessful = true;
        this.form.get('verificationCode')?.setValidators(Validators.required);
      }
    } finally {
      this.disabled = false;
    }
  }

  async onSubmit2fa() {
    if (!this.form.valid) {
      return;
    }

    this.disabled = true;
    try {
      const result = await this.authStore.login(
        this.form.value.email,
        this.form.value.password,
        this.form.value.verificationCode,
      );

      if (result.success) {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Success, message: `Login successful` },
        });
        this.router.navigate(['/organizations']);
      } else if (result.error?.deactivated_user) {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: 'Your account has been deactivated' },
        });
      } else {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: `Invalid 2FA code` },
        });
      }
    } finally {
      this.disabled = false;
    }
  }

  showQRCode() {
    this.downloadApp = false;
    this.setupVerifyAuthCode = true;

    if (this.qrCode.size > 0) {
      this.is2faSetup = false;
      const reader = new FileReader();
      reader.readAsDataURL(this.qrCode);
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const img = document.createElement('img');
        img.src = dataUrl as string;
        document.getElementById('qrCode')?.appendChild(img);
      };
    }
  }

  showVerifyAuthCode() {
    this.setupVerifyAuthCode = false;
    this.verifyAuthCode = true;
  }

  resendEmail() {
    this.totpService.totpResend(this.form?.get('email')?.value?.trim(), VerifyType.VerifyEmail).subscribe({
      next: (totpGuid) => {
        const email = this.form?.get('email')?.value?.trim();
        this.router.navigateByUrl('/verify-email', { state: { email, totpGuid, verifyType: VerifyType.VerifyEmail } });
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 4000,
          data: { toastType: ToastType.Success, message: 'Verification Code Resent' },
        });
      },
      error: () => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: 'Something went wrong, please try again.' },
        });
      },
    });
  }
}
